import axios from "vue-app/utils/axios";
import { DTODeliveryTableResponse, DTOAiReportsDataScienceResponse, DTOTopMetric, DTOComponent } from "./dto";
import { NewsletterHubResponse, ResendType } from "vue-app/components/Newsletters/Newsletters.types.ts";
import { dayjsWithTz, getUnix } from "vue-app/utils/functions.ts";
import { CHANNEL_TYPE } from "vue-app/types/enums";

export const CREATION_SOURCE = {
  DUPLICATE: 1,
  LIVE_EVENT_TASK: 2,
  AI_PROMPTER: 3,
  TEMPLATE: 4,
  ECOMMERCE_ASSISTANT: 5,
  MANUAL: 6,
} as const;

interface GetAiReportPayload {
  trackerId: number;
  language: string;
}

export const getAiReport = async ({ language, trackerId }: GetAiReportPayload) => {
  const { data } = await axios.get<DTOAiReportsDataScienceResponse>(
    `/newsletter-hub/ai-report?tracker_id=${trackerId}&language=${language}`
  );

  return data;
};

export const getTopMetrics = async (channelTypeId: number) => {
  const { data } = await axios.post<{ data: DTOTopMetric }>(
    `/json/newsletter-hub/get-top-metrics?channel_type_id=${channelTypeId}`
  );
  return data.data;
};

export const getTableData = async () => {
  const { data } = await axios.get<DTODeliveryTableResponse>("/newsletter-hub/delivery/table");
  return data.rows;
};

export const postNewCampaign = async (
  editorTypeId: number | null = null,
  templateId: number | undefined = undefined,
  creationSourceId: number | undefined = undefined
) => {
  const { data } = await axios.post<{ data: number }>("/json/newsletter-hub/create-new-campaign", {
    tracker_id: window.EDRONE_CONTEXT?.trackerId,
    ...(editorTypeId !== null && { editor_type_id: editorTypeId }),
    ...(templateId && { template_id: templateId }),
    ...(creationSourceId && { creation_source_id: creationSourceId }),
  });

  return data.data;
};

export const postNewPrivacyPolicyCampaign = async () => {
  const { data } = await axios.post<{ data: number }>("/json/newsletter-hub/create-new-privacy-policy-campaign");
  return data.data;
};

export const postNewsletterHubCreateNewWhatsappCampaign = async (
  components: DTOComponent[] | null,
  ecommerceAssistantCampaignId: number
) => {
  const { data } = await axios.post("/json/newsletter-hub/create-new-whatsapp-campaign", {
    components: components !== null ? JSON.stringify(components) : null,
    ecommerce_assistant_campaign_id: ecommerceAssistantCampaignId,
  });
  return {
    engagementId: data.data,
  };
};

export const getAiAssistantNewsletterIdeas = async (dateFrom: string, dateTo: string) => {
  const { data } = await axios.get(`/ecommerce-assistant-campaigns?date_from=${dateFrom}&date_to=${dateTo}&limit=100`);
  return data;
};

export const postNewsletterTab = async ({
  tabId,
  sort,
  sortOrder,
  search,
  channelTypeId,
  customerListTypeId,
  dateFrom,
  dateTo,
}: {
  tabId: number;
  sort: string;
  sortOrder: string;
  search: string;
  channelTypeId: number;
  customerListTypeId?: string;
  dateFrom?: Date;
  dateTo?: Date;
}) => {
  const { data } = await axios.post<{ data: NewsletterHubResponse }>("/json/newsletter-hub/get-newsletter-tab", {
    tab_id: tabId,
    sort: sort,
    sort_order: sortOrder,
    search: search,
    channel_type_id: channelTypeId,
    customer_list_type_id: customerListTypeId,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });

  return data.data;
};

export const getNewsletterHubNewsletterTabsCount = async (
  searchTerm: string,
  channelTypeId: number,
  customer_list_type_id?: number,
  dateRange?: [Date, Date]
) => {
  const { data } = await axios.get(`/json/newsletter-hub/get-newsletter-tabs-count`, {
    params: {
      search: searchTerm,
      channel_type_id: channelTypeId,
      customer_list_type_id: customer_list_type_id,
      date_from: dateRange?.[0] ? getUnix(dayjsWithTz(dateRange[0]).startOf("day").toDate()) : undefined,
      date_to: dateRange?.[1] ? getUnix(dayjsWithTz(dateRange[1]).endOf("day").toDate()) : undefined,
    },
  });
  return data;
};

export const postNewsletterHubHide = async (engagementId: number) => {
  await axios.post("/json/newsletter-hub/hide", {
    engagement_id: engagementId?.toString(),
    tracker_id: window.EDRONE_CONTEXT?.trackerId,
    agent_id: window.EDRONE_CONTEXT?.user?.agentId?.toString(),
  });
  return engagementId;
};

export const postNewsletterHubStop = async (engagementId: number) => {
  await axios.post("/json/newsletter-hub/set-to-terminated", {
    engagement_id: engagementId?.toString(),
    tracker_id: window.EDRONE_CONTEXT?.trackerId,
  });
  return engagementId;
};

export const postNewsletterHubPause = async (engagementId: number) => {
  await axios.post("/json/newsletter-hub/set-to-draft", {
    engagement_id: engagementId?.toString(),
    tracker_id: window.EDRONE_CONTEXT?.trackerId,
  });
  return engagementId;
};

export const postNewsletterHubShow = async (engagementId: number) => {
  await axios.post("/json/newsletter-hub/show", {
    engagement_id: engagementId?.toString(),
    tracker_id: window.EDRONE_CONTEXT?.trackerId,
    agent_id: window.EDRONE_CONTEXT?.user?.agentId?.toString(),
  });
  return engagementId;
};

export const postEngagementsDuplicate = async (engagementId: number, engagementTypeId: number, isLegacy?: boolean) => {
  const response = await axios.post("/engagements/duplicate", {
    engagement_type_id: engagementTypeId,
    engagement_id: engagementId,
    tracker_id: window.EDRONE_CONTEXT?.trackerId,
    isLegacy,
  });
  return response.data.engagement.engagementId;
};

export const postCreateCampaignFromEcommerceAssistant = async (id: number, channelId: CHANNEL_TYPE) => {
  const { data } = await axios.post(`/json/newsletter-hub/create-new-campaign-from-ecommerce-assistant-campaigns`, {
    ecommerce_assistant_campaigns_id: id,
    channel_type_id: channelId,
  });
  return data.data;
};

export const getIndumeterSteps = async () => {
  const { data } = await axios.get(
    `/json/newsletter-hub/get-indumeter-steps?tracker_id=${window.EDRONE_CONTEXT.trackerId}`
  );

  return data.data;
};

export const postResend = async (engagementId: number) => {
  const { data } = await axios.post("/newsletter-hub/resend", {
    engagement_id: engagementId,
    tracker_id: window.EDRONE_CONTEXT?.trackerId,
    resend_type_id: ResendType.OPENED,
  });

  return data.data;
};

export const getPrivacyPolicyNewsletter = async (): Promise<{ id: number | null }> => {
  const { data } = await axios.get<{ id: number | null }>(`/newsletter-hub/privacy-policy`);
  return data;
};

export const markScheduledPrivacyPolicyAsInformed = async () => {
  await axios.post(`/newsletter-hub/privacy-policy/informed`);
};

export const getFirstAiNewsletterSent = async (): Promise<{ sent: boolean }> => {
  const { data } = await axios.get<{ sent: boolean }>(`/newsletter-hub/status/first-ai-newsletter-sent`);
  return data;
};
